import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import isEqual from 'lodash-es/isEqual';
import { TrackedMap } from 'tracked-built-ins';

import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

class RelationshipState {
  @tracked revision = 0;
  snapshots = new TrackedMap();

  touch() {
    this.revision += 1;
  }
}

export default class RecordRelationshipsService extends Service {
  @service store;

  states = new WeakMap();

  watch(record, field, callback) {
    const state = this._stateFor(record);

    if (!state.snapshots.has(field)) {
      state.snapshots.set(field, new TrackedMap());
    }

    const snapshots = state.snapshots.get(field);
    const version = recordVersion(record);

    if (!snapshots.has(version)) {
      snapshots.set(version, this._currentState(record, field));
    }

    state.touch();

    return callback();
  }

  getDirtyRelationships(record) {
    const state = this._stateFor(record);
    const dirty = [];

    this.revisionFor(record);

    state.snapshots.forEach((versions, field) => {
      const version = recordVersion(record);
      const hasLastVersion = versions.has(version);
      const lastVersion = versions.get(version);
      const currentVersion = this._currentState(record, field);

      if (hasLastVersion && !isEqual(lastVersion, currentVersion)) {
        dirty.push(field);
      }
    });

    return dirty;
  }

  hasDirtyRelationships(record) {
    return this.getDirtyRelationships(record).length > 0;
  }

  hasDirtyFields(record) {
    return record.hasDirtyAttributes || this.hasDirtyRelationships(record);
  }

  markSaved(record) {
    const state = this._stateFor(record);

    state.snapshots.clear();
    state.touch();
  }

  rollback(record) {
    const state = this._stateFor(record);

    state.snapshots.forEach((versions, field) => {
      const lastVersion = versions.get(recordVersion(record));

      if (!lastVersion) {
        return;
      }

      record[field] = lastVersion;
    });

    state.touch();
  }

  revisionFor(record) {
    return this._stateFor(record).revision;
  }

  _stateFor(record) {
    if (!this.states.has(record)) {
      this.states.set(record, new RelationshipState());
    }

    return this.states.get(record);
  }

  _currentState(record, field) {
    const fields = this.store.schema.fields({
      type: getRecordType(record),
    });

    if (fields.get(field).kind === 'hasMany') {
      const reference = record.hasMany(field);
      const value = reference.value();

      return value ? value.slice() : [];
    }

    const reference = record.belongsTo(field);

    return reference.value();
  }
}

function recordVersion(record) {
  return 'cmsModified' in record ? record.cmsModified : undefined;
}
